import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';


interface InputState {
  initial: number;
  monthly: number;
  years: number;
  rate: number;
  variance: number;
  compoundFreq: number;
}

interface ResultState {
  final: number;
  finalLow: number;
  finalHigh: number;
  totalContributions: number;
  totalInterest: number;
}

interface ChartDataPoint {
  year: number;
  balance: number;
  contributions: number;
  interest: number;
}

export default function CompoundInterestCalculator() {
  const [inputs, setInputs] = useState<InputState>({
    initial: 10000,
    monthly: 500,
    years: 10,
    rate: 7,
    variance: 2,
    compoundFreq: 12
  });
  
  const [result, setResult] = useState<ResultState | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  const calculateCompoundInterest = () => {
    const P = Number(inputs.initial);
    const PMT = Number(inputs.monthly);
    const t = Number(inputs.years);
    const r = Number(inputs.rate) / 100;
    const variance = Number(inputs.variance) / 100;
    const n = Number(inputs.compoundFreq);

    // Calculate base scenario
    const data: ChartDataPoint[] = [];
    let balance = P;
    
    for (let year = 0; year <= t; year++) {
      if (year === 0) {
        data.push({
          year: year,
          balance: Math.round(balance),
          contributions: Math.round(P),
          interest: 0
        });
      } else {
        const periodsInYear = n;
        const ratePerPeriod = r / n;
        const paymentsInYear = 12;
        
        for (let period = 0; period < periodsInYear; period++) {
          balance = balance * (1 + ratePerPeriod);
          
          const paymentsThisPeriod = Math.floor(paymentsInYear / periodsInYear);
          balance += PMT * paymentsThisPeriod;
        }
        
        const totalContributions = P + (PMT * 12 * year);
        const totalInterest = balance - totalContributions;
        
        data.push({
          year: year,
          balance: Math.round(balance),
          contributions: Math.round(totalContributions),
          interest: Math.round(totalInterest)
        });
      }
    }

    // Calculate with variance
    let balanceLow = P;
    let balanceHigh = P;
    const rLow = r - variance;
    const rHigh = r + variance;

    for (let year = 1; year <= t; year++) {
      const periodsInYear = n;
      const rateLowPerPeriod = rLow / n;
      const rateHighPerPeriod = rHigh / n;
      const paymentsInYear = 12;
      
      for (let period = 0; period < periodsInYear; period++) {
        balanceLow = balanceLow * (1 + rateLowPerPeriod);
        balanceHigh = balanceHigh * (1 + rateHighPerPeriod);
        
        const paymentsThisPeriod = Math.floor(paymentsInYear / periodsInYear);
        balanceLow += PMT * paymentsThisPeriod;
        balanceHigh += PMT * paymentsThisPeriod;
      }
    }

    setChartData(data);
    setResult({
      final: Math.round(balance),
      finalLow: Math.round(balanceLow),
      finalHigh: Math.round(balanceHigh),
      totalContributions: Math.round(P + (PMT * 12 * t)),
      totalInterest: Math.round(balance - (P + (PMT * 12 * t)))
    });
  };

  const handleInputChange = (field: keyof InputState, value: string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-gray-100">

      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
          Compound Interest Calculator
        </h1>
        
        <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-800">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Initial Investment ($)
              </label>
              <input
                type="number"
                value={inputs.initial}
                onChange={(e) => handleInputChange('initial', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Monthly Contribution ($)
              </label>
              <input
                type="number"
                value={inputs.monthly}
                onChange={(e) => handleInputChange('monthly', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Time Period (Years)
              </label>
              <input
                type="number"
                value={inputs.years}
                onChange={(e) => handleInputChange('years', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Estimated Interest Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={inputs.rate}
                onChange={(e) => handleInputChange('rate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Interest Rate Variance (±%)
              </label>
              <input
                type="number"
                step="0.1"
                value={inputs.variance}
                onChange={(e) => handleInputChange('variance', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Compound Frequency (per year)
              </label>
              <select
                value={inputs.compoundFreq}
                onChange={(e) => handleInputChange('compoundFreq', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
              >
                <option value="1">Annually</option>
                <option value="2">Semi-annually</option>
                <option value="4">Quarterly</option>
                <option value="12">Monthly</option>
                <option value="365">Daily</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={calculateCompoundInterest}
            className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold py-3 px-6 rounded-xl shadow transition duration-200"
          >
            Calculate Investment Growth
          </button>
        </div>

        {result && (
          <>
            <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Results</h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Final Balance</p>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400">{formatCurrency(result.final)}</p>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Contributions</p>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{formatCurrency(result.totalContributions)}</p>
                </div>
                
                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Interest Earned</p>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{formatCurrency(result.totalInterest)}</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-neutral-800 p-4 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Projected Range (with ±{inputs.variance}% variance):
                </p>
                <p className="text-lg text-gray-800 dark:text-gray-200">
                  <span className="font-semibold">Low:</span> {formatCurrency(result.finalLow)} | 
                  <span className="font-semibold ml-4">High:</span> {formatCurrency(result.finalHigh)}
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Investment Growth Over Time</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="year" 
                    label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(Number(value))}
                    labelFormatter={(label) => `Year ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="contributions" stackId="a" fill="#3b82f6" name="Total Contributions" />
                  <Bar dataKey="interest" stackId="a" fill="#10b981" name="Interest Earned" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}


      </div>
    </div>
  );
}